class MobileDock extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const header = document.querySelector('.shopify-section-header');

    this.isAction = false;
    this.scrollY  = header ? parseInt(header.getBoundingClientRect().bottom) : 0;

    if (Shopify && Shopify.designMode) {
      this.scrollY = 0;
    }

    this.onDrawerCloseHandler = this.hideStickyHeader.bind(this);
    this.querySelectorAll('.dock__item[data-action]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
    document.addEventListener('menudrawer:close', this.onDrawerCloseHandler);
    document.addEventListener('searchmodal:close', this.onDrawerCloseHandler);

    // rAF-gated scroll handler: at most one class toggle per frame
    this.scrollTicking = false;
    this.onScrollHandler = () => {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.onScroll();
        this.scrollTicking = false;
      });
    };
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    this.onScroll();

    /* [ST-55 R5 2026-08-18] connectedCallback 直後は非同期CSSが未適用で offsetHeight=0 の
       場合があるため、ResizeObserver を主経路にして描画後の実寸変化を追跡する。
       load / resize / 既存 matchSmall でも再測定し、初期レイアウト確定用に2フレーム待って測る。 */
    this.onMeasuredLayoutChangeHandler = this.onMeasuredLayoutChange.bind(this);
    window.addEventListener('load', this.onMeasuredLayoutChangeHandler);
    window.addEventListener('resize', this.onMeasuredLayoutChangeHandler, false);
    document.addEventListener('matchSmall', this.onMeasuredLayoutChangeHandler);

    if ('ResizeObserver' in window) {
      this.mobileDockResizeObserver = new ResizeObserver(() => this.updateMobileDockHeight());
      this.mobileDockResizeObserver.observe(this, { box: 'border-box' });
    }

    this.setupShopifyFormsMeasurement();
    this.onMeasuredLayoutChangeHandler();
    this.measurementFrame = requestAnimationFrame(() => {
      this.onMeasuredLayoutChangeHandler();
      this.measurementFrame = requestAnimationFrame(() => {
        this.measurementFrame = null;
        this.onMeasuredLayoutChangeHandler();
      });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    document.removeEventListener('menudrawer:close', this.onDrawerCloseHandler);
    document.removeEventListener('searchmodal:close', this.onDrawerCloseHandler);
    window.removeEventListener('load', this.onMeasuredLayoutChangeHandler);
    window.removeEventListener('resize', this.onMeasuredLayoutChangeHandler);
    document.removeEventListener('matchSmall', this.onMeasuredLayoutChangeHandler);

    if (this.measurementFrame !== null) {
      cancelAnimationFrame(this.measurementFrame);
      this.measurementFrame = null;
    }

    if (this.mobileDockResizeObserver) {
      this.mobileDockResizeObserver.disconnect();
    }

    this.teardownShopifyFormsMeasurement();
  }

  /* [ST-55 R5 2026-08-18] transform による表示・退避に左右されない border-box 実寸を
     CSS変数へ反映する。display:none のデスクトップでは 0px になり、モバイルへ戻ると
     ResizeObserver / resize / matchSmall のいずれかで再び実寸へ更新される。 */
  updateMobileDockHeight() {
    const height = this.getBoundingClientRect().height;
    const measuredHeight = Number.isFinite(height) ? Math.max(0, height) : 0;

    document.documentElement.style.setProperty('--mobile-dock-height', `${measuredHeight}px`);
  }

  onMeasuredLayoutChange() {
    this.updateMobileDockHeight();
    this.syncShopifyFormsTarget();
  }

  /* [ST-55 R5 2026-08-18] Shopify Forms は body 直下へ後から追加され、位置決め本体は
     open shadow root 内で差し替わり得る。body の直下追加・削除、shadow DOM の差し替え、
     #app-embed のサイズ変化をそれぞれ監視し、実測高を --qoo-forms-stack-h へ入れる。 */
  setupShopifyFormsMeasurement() {
    this.shopifyFormsHost = null;
    this.shopifyFormsShadowRoot = null;
    this.shopifyFormsEmbed = null;
    this.shopifyFormsShadowObserver = null;
    this.shopifyFormsResizeObserver = null;

    document.documentElement.style.setProperty('--qoo-forms-stack-h', '0px');

    this.shopifyFormsBodyObserver = new MutationObserver(() => this.syncShopifyFormsTarget());
    this.shopifyFormsBodyObserver.observe(document.body, { childList: true });

    if ('ResizeObserver' in window) {
      this.shopifyFormsResizeObserver = new ResizeObserver(() => this.updateShopifyFormsStackHeight());
    }

    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('shopify-forms-embed').then(() => {
        if (this.isConnected) {
          this.syncShopifyFormsTarget();
        }
      });
    }

    this.syncShopifyFormsTarget();
  }

  syncShopifyFormsTarget() {
    /* [ST-55 R5 2026-08-18] アプリ埋め込みの id (app-embed-container-NNNNNN) は再インストール等で
       変わり得るため、セレクタに含めない。CSS 側の対象と同じ条件 (body 直下) だけで特定する。 */
    const host = document.querySelector('body > shopify-forms-embed');

    if (host !== this.shopifyFormsHost) {
      if (this.shopifyFormsShadowObserver) {
        this.shopifyFormsShadowObserver.disconnect();
        this.shopifyFormsShadowObserver = null;
      }
      if (this.shopifyFormsResizeObserver) {
        this.shopifyFormsResizeObserver.disconnect();
      }

      this.shopifyFormsHost = host;
      this.shopifyFormsShadowRoot = null;
      this.shopifyFormsEmbed = null;
    }

    const shadowRoot = host && host.shadowRoot ? host.shadowRoot : null;

    if (shadowRoot !== this.shopifyFormsShadowRoot) {
      if (this.shopifyFormsShadowObserver) {
        this.shopifyFormsShadowObserver.disconnect();
      }

      this.shopifyFormsShadowRoot = shadowRoot;
      this.shopifyFormsShadowObserver = null;

      if (shadowRoot) {
        this.shopifyFormsShadowObserver = new MutationObserver(() => this.syncShopifyFormsTarget());
        this.shopifyFormsShadowObserver.observe(shadowRoot, {
          attributes: true,
          childList: true,
          subtree: true
        });
      }
    }

    const appEmbed = shadowRoot ? shadowRoot.querySelector('div#app-embed') : null;

    if (appEmbed !== this.shopifyFormsEmbed) {
      if (this.shopifyFormsResizeObserver) {
        this.shopifyFormsResizeObserver.disconnect();
      }

      this.shopifyFormsEmbed = appEmbed;

      if (appEmbed && this.shopifyFormsResizeObserver) {
        this.shopifyFormsResizeObserver.observe(appEmbed, { box: 'border-box' });
      }
    }

    this.updateShopifyFormsStackHeight();
  }

  updateShopifyFormsStackHeight() {
    const height = this.shopifyFormsEmbed && this.shopifyFormsEmbed.isConnected
      ? this.shopifyFormsEmbed.getBoundingClientRect().height
      : 0;
    const measuredHeight = Number.isFinite(height) ? Math.max(0, height) : 0;

    document.documentElement.style.setProperty('--qoo-forms-stack-h', `${measuredHeight}px`);
  }

  teardownShopifyFormsMeasurement() {
    if (this.shopifyFormsBodyObserver) {
      this.shopifyFormsBodyObserver.disconnect();
    }
    if (this.shopifyFormsShadowObserver) {
      this.shopifyFormsShadowObserver.disconnect();
    }
    if (this.shopifyFormsResizeObserver) {
      this.shopifyFormsResizeObserver.disconnect();
    }
  }

  onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (scrollTop >= this.scrollY) {
      this.classList.add('is-active');
    }
    else {
      this.classList.remove('is-active');
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;
    // Search handles the hidden header itself (instant, same-frame) — the
    // animated waitStickyHeader() reveal would paint the header + menu
    // sliding in over mid-page content before the modal opens, a slow
    // two-step.
    const delay = target.dataset.action === 'search' ? 0 : this.waitStickyHeader();

    switch (target.dataset.action) {
      case 'cart':
        this.isAction = true;

        const miniCart = document.querySelector('mini-cart');
        if (miniCart) {
          setTimeout(() => {
            miniCart.open();
            document.activeElement.blur();
          }, delay);
        }
        else {
          window.location.href = theme.routes.cart_url;
        }
        break;

      case 'menu':
        this.isAction = true;

        const headerDrawer = document.querySelector('header-drawer');
        if (headerDrawer) {
          setTimeout(() => {
            headerDrawer.openMenuDrawer();
          }, delay);
        }
        break;

      case 'search':
        this.isAction = true;

        const searchModals = document.querySelectorAll('search-modal');
        if (searchModals.length > 0) {
          const openModals = () => {
            searchModals.forEach((searchModal) => {
              const style = window.getComputedStyle(searchModal);
              if (style.display === 'none') {
                return;
              }

              searchModal.open();
              searchModal.querySelector('input:not([type="hidden"])').focus();
            });
          };

          // The search modal is anchored inside the header, so an off-screen
          // header cannot be left as-is: a non-sticky header only exists at
          // the page top, and a hidden sticky header carries a translateY
          // transform that becomes the containing block for the modal's
          // position:fixed (it would open off-screen — blank overlay, no
          // input). Resolve the header in the SAME frame the modal opens,
          // with the fade-in suppressed: any separate "prepare the header
          // first" step paints an intermediate state (header + menu sliding
          // in over mid-page content) that reads as a slow two-step open.
          const headerSection = document.querySelector('.shopify-section-header');
          const stickyHeader = document.querySelector('sticky-header');
          if (headerSection && !this.isElementVisible(headerSection)) {
            document.body.classList.add('search-modal--instant');

            if (stickyHeader === null) {
              // Non-sticky: the header lives at the page top — jump there.
              window.scrollTo({ top: 0, behavior: 'instant' });
            }
            else {
              // Sticky: reveal in place, minus the .animate class so the
              // transform snaps instead of sliding — header and the modal
              // covering it reach the screen in one paint, and the visitor
              // keeps their scroll position for when the modal closes.
              stickyHeader.reveal();
              stickyHeader.header.classList.remove('animate');
            }

            // Open on the next animation frame: AFTER this click has finished
            // bubbling (opening synchronously lets the modal's outside-click
            // handler see the very same click and close it again instantly),
            // but BEFORE the browser paints — so there is still no flash of
            // the revealed header or page-top content.
            requestAnimationFrame(() => openModals());
            // Keep the class until the modal closes: removing it while open
            // makes the fade-in rule match again, which RESTARTS the CSS
            // animation — the modal blinks to opacity 0 and fades back.
            document.addEventListener('searchmodal:close', () => {
              document.body.classList.remove('search-modal--instant');
            }, { once: true });
          }
          else {
            setTimeout(openModals, delay);
          }
        }
        else {
          window.location.href = theme.routes.search_url;
        }
        break;
    }
  }

  hideStickyHeader() {
    const header = document.querySelector('sticky-header');
    if (header === null) return;

    if (theme.config.mqlSmall && this.isAction && header.sticky()) {
      setTimeout(() => {
        header.hide();
        this.isAction = false;
      }, 500);
    }
  }

  waitStickyHeader() {
    const header = document.querySelector('sticky-header');
    if (header === null) return;

    if (!header.sticky() && !this.isElementVisible(header)) {
      header.hide();
      setTimeout(() => header.reveal());

      return 250;
    }

    return 0;
  }

  isElementVisible(element) {
    const rect = element.getBoundingClientRect(),
        width  = window.innerWidth || document.documentElement.clientWidth,
        height = window.innerHeight || document.documentElement.clientHeight,
        efp    = function (x, y) { return document.elementFromPoint(x, y) };

    // Return false if it's not in the viewport
    if (rect.right < 0 || rect.bottom < 0 || rect.left > width || rect.top > height) {
      return false;
    }

    // Return true if any of its four corners are visible
    return (
      element.contains(efp(rect.left,  rect.top))
      || element.contains(efp(rect.right, rect.top))
      || element.contains(efp(rect.right, rect.bottom))
      || element.contains(efp(rect.left,  rect.bottom))
      || element.parentNode.contains(efp(rect.left,  rect.top))
      || element.parentNode.contains(efp(rect.right, rect.top))
      || element.parentNode.contains(efp(rect.right, rect.bottom))
      || element.parentNode.contains(efp(rect.left,  rect.bottom))
    );
  }
}
customElements.define('mobile-dock', MobileDock);
